import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import LanguageCurrencyModalProps, { LanguageCurrencyModalHandle } from './props';
import { isOuterClick } from '../../../../../utils';
import "./styles.scss";

const LanguageCurrencyModal = forwardRef<LanguageCurrencyModalHandle, LanguageCurrencyModalProps>((props, ref) => {
  const { isOpen, onClose, children } = props;
  const modalRef = useRef<HTMLDivElement>(null);
  const [isClosing, setIsClosing] = useState(false);

  const handleAnimationEnd = () => {
    if (isClosing) {
      setIsClosing(false);
      onClose();
    }
  };

  const closeModal = useCallback( () => {
    setIsClosing(true);
  }, []);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (
      modalRef.current &&
      isOuterClick(e.target as HTMLElement, modalRef.current as HTMLElement)
    ) {
      closeModal();
    }
  }, [closeModal]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeModal();
    }
  }, [closeModal]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleClickOutside, handleKeyDown]);

  useImperativeHandle(ref, () => ({
    closeModal
  }));

  if (!isOpen && !isClosing) return null;

  return (
    <div
      className={`language-currency-modal ${isClosing ? 'modal-exit' : ''}`}
      ref={modalRef}
      style={{
        display: isOpen ? 'flex' : /* istanbul ignore next */ 'none',
        flexDirection: 'column'
      }}
      onAnimationEnd={handleAnimationEnd}
    >
      {children}
    </div>
  );
});

LanguageCurrencyModal.displayName = 'LanguageCurrencyModal';

export default LanguageCurrencyModal;
